import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useNativeCall } from '@/hooks/useNativeCall';
import { useNativeEmail } from '@/hooks/useNativeEmail';
import { EVENT_NAME } from '@/lib/gtm/const';
import { useRegionProvider } from '@/providers/RegionProvider';

import * as S from './ContactInfo.styles';

export interface ContactInfoProps {
  contactInfo: {
    email: string;
    phoneNumber: string;
    weekdayHours: string;
    weekendHours: string;
  };
}

export const ContactInfo = ({
  contactInfo: { email, phoneNumber, weekdayHours, weekendHours },
}: ContactInfoProps) => {
  const { country } = useRegionProvider();
  const { sendEvent } = useGoogleAnalytics();
  const { initiateNativeCall } = useNativeCall(country.code);
  const { initiateNativeEmail } = useNativeEmail(country.code);

  const callCustomerService = () => {
    sendEvent({
      event: EVENT_NAME.CONTACT,
      contact_type: 'phone',
    });
    initiateNativeCall();
  };

  const emailCustomerService = () => {
    sendEvent({
      event: EVENT_NAME.CONTACT,
      contact_type: 'email',
    });
    initiateNativeEmail();
  };

  const displayHours =
    country.code === 'US' || country.code === 'CA'
      ? `${weekdayHours} EST --- ${weekendHours}`
      : `${weekdayHours}, ${weekendHours}`;

  return (
    <S.ContactInfoWrapper
      alignItems={{ sx: 'center', md: 'stretch' }}
      data-testid='ContactInfoWrapper'
      flexDirection={{ sx: 'column', md: 'row' }}
      justifyContent='center'
    >
      {phoneNumber && (
        <Flex
          alignItems='center'
          color='primary'
          data-test-id='ContactInfoFlex'
          mx={1}
        >
          <S.PointerWrapper
            data-testid='ContactInfoPhoneNumber'
            onClick={() => callCustomerService()}
          >
            <Typography color='primary' scale='h6'>
              {phoneNumber}
            </Typography>
          </S.PointerWrapper>
        </Flex>
      )}
      <Typography
        color='primary'
        data-test-id='ContactInfoOpeningHours'
        mx={1}
        scale='h6'
        textTransform='uppercase'
      >
        {displayHours}
      </Typography>
      <S.PointerWrapper
        data-testid='ContactInfoEmailAddress'
        onClick={() => emailCustomerService()}
      >
        <Typography color='primary' scale='h6' textTransform='uppercase'>
          {email}
        </Typography>
      </S.PointerWrapper>
    </S.ContactInfoWrapper>
  );
};
